import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { SUPPORTED_LOCALES } from 'variables/main';
import getLocale from 'helpers/getLocale';
import {
  SITE_CONFIG,
  LANG_TO_COUNTRY,
  generateOrganizationSchema,
  generateWebSiteSchema,
  generateBreadcrumbSchema,
  buildHreflangUrls,
  buildOgImageUrl,
  getBaseUrl
} from 'utils/seo';
import imageUrl from 'assets/images/preview.png';
import professionCases from 'helpers/professionCases';

// Google truncates around 160 chars and Ahrefs flags anything longer. Titles
// interpolated with long profession/article names routinely overshoot, so trim
// on a word boundary at emission rather than editing 100+ translation strings.
const clampDescription = (value, max = 160) => {
  if (typeof value !== 'string' || value.length <= max) return value;
  const cut = value.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.-]+$/, '') + '…';
};


/**
 * Enhanced SEO Component
 * Provides comprehensive SEO support including:
 * - Standard meta tags (title, description, keywords)
 * - Open Graph tags for social sharing
 * - Twitter Card tags
 * - JSON-LD structured data
 * - Canonical URLs
 * - Hreflang tags for multilingual support
 * - Robots meta tags
 */
const SEO = ({
  // Page identification
  page = '',
  
  // Basic meta
  title,
  description,
  keywords,
  
  // Dynamic content for template pages
  name,
  declensions,
  
  // Custom meta override
  meta = {},
  
  // Images
  metaImageUrl,
  
  // URLs
  canonicalUrl,
  ampUrl,
  noIndex = false,
  noFollow = false,
  
  // Structured data
  schema = null,
  additionalSchemas = [],
  breadcrumbs = [],
  
  // Article specific
  article = null,
  publishedTime,
  modifiedTime,
  author,
  section,
  tags = [],
  
  // Page type for Open Graph
  ogType = 'website',
  
  // Pagination
  prevPage,
  nextPage,
  
  // Multilingual
  includeHreflang = false
}) => {
  const t = useTranslate('HELMET');
  const locale = getLocale();
  const currentYear = React.useMemo(() => new Date().getFullYear(), []);

  // BCP 47 compliant html lang: "en-GB", "de-DE", "uk-UA" (region uppercase for validity)
  const htmlLang = React.useMemo(() => {
    const lang = locale || 'uk';
    // Map internal 'ua' (Ukraine locale) to ISO 639-1 'uk' (Ukrainian language)
    const languageCode = lang === 'ua' ? 'uk' : lang;
    const country = LANG_TO_COUNTRY[lang === 'ua' ? 'ua' : lang];
    if (country) {
      return `${languageCode}-${country}`; // e.g. en-GB, de-DE, uk-UA
    }
    return languageCode === 'uk' ? 'uk' : languageCode;
  }, [locale]);
  
  // Base URL
  const baseUrl = React.useMemo(() => getBaseUrl(), []);
  
  // Brand is baked into config at build time (config.profitroll.json /
  // config.jobalto.json); each brand has its own build.
  const siteName = SITE_CONFIG.name;
  
  // Current URL for canonical
  const currentUrl = React.useMemo(() => {
    if (canonicalUrl) return canonicalUrl;
    if (typeof window === 'undefined') return '';
    
    const path = window.location.pathname;
    
    // During react-snap or in browser, build proper canonical URL
    // Check if path starts with a locale prefix (e.g., /en-gb/, /de-de/)
    const isLocalizedPath = /^\/[a-z]{2}-[a-z]{2}(\/|$)/i.test(path);
    
    if (isLocalizedPath) {
      // Jobalto.com for localized paths
      return `https://jobalto.com${path}`;
    } else {
      // Profitroll.com.ua for non-localized paths
      return `https://profitroll.com.ua${path}`;
    }
  }, [canonicalUrl]);

  // Meta tags configuration per page type
  const metaTags = React.useMemo(
    () => ({
      home: {
        title: t('homeTitle'),
        description: t('homeDescription'),
        keywords: t('homeKeywords'),
        ogTitle: t('homeOgTitle'),
        ogDescription: t('homeOgDescription')
      },
      assessment: {
        title: t('assessmentTitle'),
        description: t('assessmentDescription'),
        keywords: t('assessmentKeywords'),
        ogTitle: t('assessmentOgTitle'),
        ogDescription: t('assessmentOgDescription')
      },
      professions: {
        title: t('professionsTitle', { currentYear }),
        description: t('professionsDescription'),
        keywords: t('professionsKeywords'),
        ogTitle: t('professionsOgTitle'),
        ogDescription: t('professionsOgDescription')
      },
      professionsAll: {
        title: meta?.title || t('professionsAllTitle'),
        description: meta?.description || t('professionsAllDescription'),
        keywords: meta?.keywords || t('professionsKeywords'),
        ogTitle: meta?.ogTitle || meta?.title || t('professionsAllOgTitle'),
        ogDescription: meta?.ogDescription || meta?.description || t('professionsAllOgDescription')
      },
      profession: {
        // Slide-mode pages override these via `meta` so each slide URL is its
        // own indexable page with a distinct title and description.
        title: meta?.title || t('professionTitle', { name, currentYear }),
        description: meta?.description || t('professionDescription', { name }),
        keywords:
          meta?.keywords ||
          t('professionKeywords', { ...professionCases({ declensions, name }), name }),
        ogTitle: meta?.ogTitle || meta?.title || t('professionOgTitle', { name }),
        ogDescription: meta?.ogDescription || meta?.description || t('professionOgDescription', { name })
      },
      skills: {
        title: t('skillsTitle', { currentYear }),
        description: t('skillsDescription'),
        keywords: t('skillsKeywords'),
        ogTitle: t('skillsOgTitle'),
        ogDescription: t('skillsOgDescription')
      },
      skill: {
        title: t('skillTitle', { name, currentYear }),
        description: t('skillDescription', { name }),
        keywords: t('skillKeywords', { name }),
        ogTitle: t('skillOgTitle', { name }),
        ogDescription: t('skillOgDescription', { name })
      },
      articles: {
        title: t('postsTitle'),
        description: t('postsDescription'),
        keywords: t('postsKeywords') || t('homeKeywords'),
        ogTitle: t('postsOgTitle'),
        ogDescription: t('postsOgDescription')
      },
      article: {
        title: meta?.title || t('postOgTitle'),
        description: meta?.description || t('postDescription'),
        keywords: meta?.keywords || t('homeKeywords'),
        ogTitle: meta?.ogTitle || meta?.title || t('postOgTitle'),
        ogDescription: meta?.ogDescription || meta?.description || t('postOgDescription')
      },
      vacancies: {
        title: t('vacanciesTitle'),
        description: t('vacanciesDescription'),
        keywords: t('vacanciesKeywords') || t('homeKeywords'),
        ogTitle: t('vacanciesOgTitle'),
        ogDescription: t('vacanciesOgDescription')
      },
      vacancy: {
        title: meta?.title || `${name} - ${t('vacancyOgTitle')}`,
        description: meta?.description || t('vacancyDescription'),
        keywords: meta?.keywords || t('homeKeywords'),
        ogTitle: meta?.ogTitle || meta?.title || `${name} - ${t('vacancyOgTitle')}`,
        ogDescription: meta?.ogDescription || meta?.description || t('vacancyOgDescription')
      },
      courses: {
        title: t('coursesTitle'),
        description: t('coursesDescription'),
        keywords: t('coursesKeywords') || t('homeKeywords'),
        ogTitle: t('coursesOgTitle'),
        ogDescription: t('coursesOgDescription')
      },
      course: {
        title: meta?.title || `${name} - ${t('courseOgTitle')}`,
        description: meta?.description || t('courseDescription'),
        keywords: meta?.keywords || t('homeKeywords'),
        ogTitle: meta?.ogTitle || meta?.title || `${name} - ${t('courseOgTitle')}`,
        ogDescription: meta?.ogDescription || meta?.description || t('courseOgDescription')
      },
      consultants: {
        title: t('consultantsTitle') || `${SITE_CONFIG.name} | Career Consultants`,
        description: t('consultantsDescription'),
        keywords: t('consultantsKeywords') || t('homeKeywords'),
        ogTitle: t('consultantsOgTitle'),
        ogDescription: t('consultantsOgDescription')
      },
      consultant: {
        title: meta?.title || `${name} - ${t('consultantOgTitle')}`,
        description: meta?.description || t('consultantDescription'),
        keywords: meta?.keywords || t('homeKeywords'),
        ogTitle: meta?.ogTitle || meta?.title || `${name} - ${t('consultantOgTitle')}`,
        ogDescription: meta?.ogDescription || meta?.description || t('consultantOgDescription')
      },
      mentors: {
        title: t('mentorsTitle') || `${SITE_CONFIG.name} | Career Mentors`,
        description: t('mentorsDescription'),
        keywords: t('mentorsKeywords') || t('homeKeywords'),
        ogTitle: t('mentorsOgTitle'),
        ogDescription: t('mentorsOgDescription')
      },
      mentor: {
        title: meta?.title || `${name} - ${t('mentorOgTitle')}`,
        description: meta?.description || t('mentorDescription'),
        keywords: meta?.keywords || t('homeKeywords'),
        ogTitle: meta?.ogTitle || meta?.title || `${name} - ${t('mentorOgTitle')}`,
        ogDescription: meta?.ogDescription || meta?.description || t('mentorOgDescription')
      },
      educationPrograms: {
        title: t('educationProgramsTitle'),
        description: t('educationProgramsDescription'),
        keywords: t('educationProgramsKeywords') || t('homeKeywords'),
        ogTitle: t('educationProgramsOgTitle'),
        ogDescription: t('educationProgramsOgDescription')
      },
      educationProgram: {
        title: meta?.title || `${name} - ${t('educationProgramOgTitle')}`,
        description: meta?.description || t('educationProgramDescription'),
        keywords: meta?.keywords || t('homeKeywords'),
        ogTitle: meta?.ogTitle || meta?.title || `${name} - ${t('educationProgramOgTitle')}`,
        ogDescription: meta?.ogDescription || meta?.description || t('educationProgramOgDescription')
      },
      platforms: {
        title: t('platformTitle'),
        description: t('platformDescription'),
        keywords: t('platformKeywords') || t('homeKeywords'),
        ogTitle: t('platformOgTitle'),
        ogDescription: t('platformOgDescription')
      },
      platform: {
        title: meta?.title || `${name} - ${t('platformOgTitle')}`,
        description: meta?.description || t('platformDescription'),
        keywords: meta?.keywords || t('homeKeywords'),
        ogTitle: meta?.ogTitle || meta?.title || `${name} - ${t('platformOgTitle')}`,
        ogDescription: meta?.ogDescription || meta?.description || t('platformOgDescription')
      },
      company: {
        title: meta?.title || `${name} - ${t('companyOgTitle')}`,
        description: meta?.description || t('companyDescription'),
        keywords: meta?.keywords || t('homeKeywords'),
        ogTitle: meta?.ogTitle || meta?.title || `${name} - ${t('companyOgTitle')}`,
        ogDescription: meta?.ogDescription || meta?.description || t('companyOgDescription')
      },
      about: {
        title: t('aboutUsTitle'),
        description: t('aboutUsDescription'),
        keywords: t('aboutUsKeywords') || t('homeKeywords'),
        ogTitle: t('aboutUsOgTitle'),
        ogDescription: t('aboutUsOgDescription')
      },
      policy: {
        title: meta?.title || t('policyTitle') || `${SITE_CONFIG.name} | Policy`,
        description: meta?.description || t('policyDescription') || t('homeDescription'),
        keywords: t('homeKeywords'),
        ogTitle: meta?.ogTitle || t('policyOgTitle') || t('policyTitle'),
        ogDescription: meta?.ogDescription || t('policyDescription') || t('homeDescription')
      },
      chat: {
        title: t('chatTitle'),
        description: t('chatDescription'),
        keywords: t('chatKeywords') || t('homeKeywords'),
        ogTitle: t('chatOgTitle'),
        ogDescription: t('chatOgDescription')
      },
      signIn: {
        title: t('signInTitle'),
        description: t('signInDescription'),
        keywords: t('homeKeywords'),
        ogTitle: t('signInOgTitle'),
        ogDescription: t('signInOgDescription')
      },
      signUp: {
        title: t('signUpTitle'),
        description: t('signUpDescription'),
        keywords: t('homeKeywords'),
        ogTitle: t('signUpOgTitle'),
        ogDescription: t('signUpOgDescription')
      },
      profile: {
        title: t('profileTitle'),
        description: t('profileDescription'),
        keywords: t('homeKeywords'),
        ogTitle: t('profileOgTitle'),
        ogDescription: t('profileOgDescription')
      },
      orders: {
        title: t('ordersTitle'),
        description: t('ordersDescription'),
        keywords: t('homeKeywords'),
        ogTitle: t('ordersOgTitle'),
        ogDescription: t('ordersOgDescription')
      },
      order: {
        title: t('orderTitle'),
        description: t('orderDescription'),
        keywords: t('homeKeywords'),
        ogTitle: t('orderOgTitle'),
        ogDescription: t('orderOgDescription')
      },
      subscription: {
        title: t('subscribeTypeTitle'),
        description: t('subscribeTypeDescription'),
        keywords: t('homeKeywords'),
        ogTitle: t('subscribeTypeOgTitle'),
        ogDescription: t('subscribeTypeOgDescription')
      },
      localizations: {
        title: t('localizationsTitle'),
        description: t('localizationsDescription'),
        keywords: t('homeKeywords'),
        ogTitle: t('localizationsOgTitle'),
        ogDescription: t('localizationsOgDescription')
      },
      resetPassword: {
        title: t('resetPasswordTitle'),
        description: t('resetPasswordDescription'),
        keywords: t('homeKeywords'),
        ogTitle: t('resetPasswordOgTitle'),
        ogDescription: t('resetPasswordOgDescription')
      },
      '404': {
        title: t('page404Title'),
        description: t('page404Description'),
        keywords: t('homeKeywords'),
        ogTitle: t('page404OgTitle'),
        ogDescription: t('page404OgDescription')
      },
      '': {
        title: title || meta?.title || t('homeTitle'),
        description: description || meta?.description || t('homeDescription'),
        keywords: keywords || meta?.keywords || t('homeKeywords'),
        ogTitle: meta?.ogTitle || title || meta?.title || t('homeOgTitle'),
        ogDescription: meta?.ogDescription || description || meta?.description || t('homeOgDescription')
      }
    }),
    [currentYear, t, declensions, name, meta, title, description, keywords]
  );

  const currentMeta = metaTags[page] || metaTags[''];

  // Append site name to titles if not already present, then cap to 60 chars
  // (Google truncates SERP titles around 60 chars; longer titles trigger
  // Ahrefs "Title too long" and risk Google rewriting the SERP title).
  const finalTitle = React.useMemo(() => {
    const title = currentMeta.title || '';
    const withBrand = title && !title.includes(siteName) ? `${title} - ${siteName}` : title;
    if (withBrand.length <= 60) return withBrand;
    // Try keeping the brand suffix by truncating the title portion.
    const suffix = ` - ${siteName}`;
    if (title && withBrand.endsWith(suffix)) {
      const room = 60 - suffix.length - 1; // -1 for the ellipsis
      if (room > 10) {
        return `${title.slice(0, room).trimEnd()}…${suffix}`;
      }
    }
    return `${withBrand.slice(0, 59).trimEnd()}…`;
  }, [currentMeta.title, siteName]);

  // og:title can be longer than <title>, but keep it consistent with the SERP
  // title to avoid Ahrefs "Page and SERP titles do not match".
  const finalOgTitle = React.useMemo(() => {
    return finalTitle;
  }, [finalTitle]);

  // Final image URL
  const finalImageUrl = React.useMemo(() => {
    return buildOgImageUrl(metaImageUrl) || `${baseUrl}${imageUrl}`;
  }, [metaImageUrl, baseUrl]);

  // Robots meta content
  const robotsContent = React.useMemo(() => {
    const parts = [];
    parts.push(noIndex ? 'noindex' : 'index');
    parts.push(noFollow ? 'nofollow' : 'follow');
    return parts.join(', ');
  }, [noIndex, noFollow]);

  // Hreflang URLs
  const hreflangUrls = React.useMemo(() => {
    if (!includeHreflang) return [];
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    return buildHreflangUrls(path, SUPPORTED_LOCALES);
  }, [includeHreflang]);

  // BreadcrumbList JSON-LD is emitted by `components/BreadCrumbs` (which has
  // the i18n context to localize "Home"/"Professions"/etc. into "Головна"/
  // "Мапа професій"/…). Emitting a second copy here from the raw English
  // breadcrumb-key labels produced a duplicate BreadcrumbList per page —
  // visible on every prerendered detail page (e.g. /professions/<slug>/<id>
  // had two scripts: one Ukrainian-localized, one English-only). Stop
  // generating it from SEO; BreadCrumbs is the single source of truth.

  // Combine all schemas
  const allSchemas = React.useMemo(() => {
    const schemas = [];

    // Add organization schema on home page
    if (page === 'home' || page === '') {
      schemas.push(generateOrganizationSchema());
      schemas.push(generateWebSiteSchema('/professions'));
    }

    // Add page-specific schema
    if (schema) {
      schemas.push(schema);
    }
    
    // Add additional schemas
    if (additionalSchemas.length > 0) {
      schemas.push(...additionalSchemas);
    }
    
    return schemas.filter(Boolean);
  }, [page, schema, additionalSchemas]);

  // Determine OG type based on page
  const finalOgType = React.useMemo(() => {
    if (ogType) return ogType;
    if (page === 'article' || article) return 'article';
    if (page === 'profession' || page === 'vacancy') return 'article';
    if (page === 'consultant' || page === 'mentor') return 'profile';
    return 'website';
  }, [ogType, page, article]);

  // Determine OG locale using LANG_TO_COUNTRY mapping
  const ogLocale = React.useMemo(() => {
    const currentLocale = locale || 'uk';
    // Normalize 'uk' to 'ua' for mapping lookup (LANG_TO_COUNTRY uses 'ua')
    const localeKey = currentLocale === 'uk' ? 'ua' : currentLocale;
    const countryCode = LANG_TO_COUNTRY[localeKey] || 'GB';
    return `${currentLocale}_${countryCode}`;
  }, [locale]);

  // Dev-time check that meta description length is in the 120–160 sweet spot.
  // Ahrefs flags <120 as "too short". Production builds skip the warning.
  if (process.env.NODE_ENV !== 'production') {
    const desc = currentMeta.description || '';
    if (desc && (desc.length < 120 || desc.length > 160)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[SEO] description length ${desc.length} is outside 120–160 ` +
          `for page="${page}": ${desc.slice(0, 80)}...`
      );
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // Imperative metadata management.
  //
  // History: this component used to render <title>/<meta>/<link> as JSX
  // children, first inside react-helmet-async's <Helmet>, then as React
  // Fragment children expecting React 19's native metadata hoisting to
  // handle <head> placement and dedup. Both routes produced duplicate
  // tags on re-render — react-helmet-async because its React-19 detection
  // is unreliable in the prerender environment, and React 19's hoisting
  // because it doesn't dedup the same tag emitted from multiple commits
  // of the same component (we observed up to 5 duplicate <title>s and
  // 4 duplicate canonicals on the home page).
  //
  // The fix is to skip React's render path for metadata entirely: build
  // every tag imperatively in a single useEffect keyed by the final
  // values, tag-marking each managed element with data-seo-managed so
  // subsequent runs can clear+rebuild atomically. The component itself
  // returns null — there is no metadata in the React tree, so there is
  // nothing for hoisting to duplicate.
  //
  // For prerender: puppeteer mounts React, this useEffect runs, head
  // ends up with one of each tag. page.content() captures cleanly.
  // ───────────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    const head = document.head;
    const SEO_MARK = 'data-seo-managed';

    // Per-tag-slot replace (NOT bulk clear-then-add).
    //
    // Several pages render multiple <Helmet> instances simultaneously
    // (e.g. home renders <Helmet page="home"> and Onboarding renders
    // <Helmet meta={...}> at the same time). A naive "clear all
    // data-seo-managed then re-add mine" wipes the *other* instance's
    // tags too — the auxiliary Helmet has no hreflang/canonical/etc. and
    // its useEffect strips the primary's contributions to those slots.
    //
    // Instead each setMeta/setLink upserts one specific slot, identified
    // by its attribute key (e.g. `meta[name="description"]`), without
    // touching unrelated slots. Group tags (hreflang, JSON-LD) are
    // handled below with their own scoped clear-then-add.

    const setMeta = (keyAttr, keyVal, content) => {
      const sel = `meta[${keyAttr}="${keyVal}"]`;
      const existing = head.querySelector(sel);
      if (!content) {
        if (existing) existing.remove();
        return;
      }
      if (existing) {
        existing.setAttribute('content', String(content));
        existing.setAttribute(SEO_MARK, '');
        return;
      }
      const el = document.createElement('meta');
      el.setAttribute(keyAttr, keyVal);
      el.setAttribute('content', String(content));
      el.setAttribute(SEO_MARK, '');
      head.appendChild(el);
    };

    const setLinkSingleton = (rel, href, extraAttrs = {}) => {
      const sel = `link[rel="${rel}"]`;
      const existing = head.querySelector(sel);
      if (!href) {
        if (existing) existing.remove();
        return;
      }
      const target = existing || document.createElement('link');
      target.setAttribute('rel', rel);
      target.setAttribute('href', href);
      for (const [k, v] of Object.entries(extraAttrs)) target.setAttribute(k, v);
      target.setAttribute(SEO_MARK, '');
      if (!existing) head.appendChild(target);
    };

    // ────────────────────────────────────────────────────────────────
    // <html lang> — same imperative-mutation strategy.
    if (htmlLang) document.documentElement.lang = htmlLang;

    // <title> — document singleton.
    document.title = finalTitle;

    // Basic meta — one slot each.
    setMeta('name', 'description', clampDescription(currentMeta.description));
    setMeta('name', 'keywords', currentMeta.keywords);
    setMeta('name', 'robots', robotsContent);
    setMeta('name', 'googlebot', robotsContent);

    // Canonical / AMP / pagination — one slot each.
    setLinkSingleton('canonical', canonicalUrl || currentUrl);
    setLinkSingleton('amphtml', ampUrl);
    setLinkSingleton('prev', prevPage);
    setLinkSingleton('next', nextPage);

    // Open Graph — each property has its own slot.
    setMeta('property', 'og:site_name', siteName);
    setMeta('property', 'og:type', finalOgType);
    setMeta('property', 'og:title', finalOgTitle);
    setMeta('property', 'og:description', currentMeta.ogDescription);
    setMeta('property', 'og:image', finalImageUrl);
    setMeta('property', 'og:image:secure_url', finalImageUrl);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '627');
    setMeta('property', 'og:image:alt', finalOgTitle);
    setMeta('property', 'og:url', canonicalUrl || currentUrl);
    setMeta('property', 'og:locale', ogLocale);

    // Article-specific OG. When the page is not article-ish, clear any
    // stale ones a previous article render may have left behind.
    const isArticle = page === 'article' || article;
    setMeta('property', 'article:published_time', isArticle ? publishedTime : null);
    setMeta('property', 'article:modified_time', isArticle ? modifiedTime : null);
    setMeta('property', 'article:author', isArticle ? author : null);
    setMeta('property', 'article:section', isArticle ? section : null);
    // article:tag is a multi-value group — clear+re-add only when
    // we're on an article page.
    head.querySelectorAll('meta[property="article:tag"]').forEach((el) => el.remove());
    if (isArticle) {
      for (const tag of tags) {
        const el = document.createElement('meta');
        el.setAttribute('property', 'article:tag');
        el.setAttribute('content', tag);
        el.setAttribute(SEO_MARK, '');
        head.appendChild(el);
      }
    }

    // Twitter cards — one slot each.
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:site', SITE_CONFIG.twitterHandle);
    setMeta('name', 'twitter:creator', SITE_CONFIG.twitterHandle);
    setMeta('name', 'twitter:title', finalOgTitle);
    setMeta('name', 'twitter:description', currentMeta.ogDescription);
    setMeta('name', 'twitter:image', finalImageUrl);
    setMeta('name', 'twitter:image:alt', finalOgTitle);

    // Hreflang — group slot. Only manage when this instance actually has
    // a non-empty list; otherwise leave whatever the primary SEO put
    // there alone.
    if (hreflangUrls.length > 0) {
      head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((el) => el.remove());
      for (const entry of hreflangUrls) {
        const el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', entry.locale);
        el.setAttribute('href', entry.url);
        el.setAttribute(SEO_MARK, '');
        head.appendChild(el);
      }
    }

    // JSON-LD — group slot. Same scoping: only replace when this
    // instance has schemas, so auxiliary SEO instances (Onboarding steps
    // etc.) don't strip the primary SEO's Organization / WebSite /
    // BreadcrumbList schemas.
    if (allSchemas.length > 0) {
      head
        .querySelectorAll('script[type="application/ld+json"][data-seo-managed]')
        .forEach((el) => el.remove());
      for (const schemaData of allSchemas) {
        const el = document.createElement('script');
        el.setAttribute('type', 'application/ld+json');
        el.setAttribute(SEO_MARK, '');
        el.textContent = JSON.stringify(schemaData);
        head.appendChild(el);
      }
    }

    // Signal to puppeteer (waitForFunction) that page-specific SEO state
    // has been committed to the DOM. Two RAFs let the browser commit a
    // paint between this effect and the flag flip so the flag never
    // beats the visual that prerender is supposed to capture.
    //
    // The timer races the RAF chain because Chrome throttles rAF to ~1fps in
    // backgrounded pages, and the prerenderer renders several pages at once
    // so all but one are backgrounded (measured: 1 tick/s vs 55). That turned
    // a two-frame wait into a ~2s wait on nearly every render. 50ms is well
    // past the paint the two RAFs are there to allow, and whichever path wins
    // sets the same flag.
    let raf2 = 0;
    const markReady = () => {
      window.__PRERENDER_READY__ = true;
    };
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(markReady);
    });
    const readyTimer = window.setTimeout(markReady, 50);

    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      window.clearTimeout(readyTimer);
    };
  }, [
    htmlLang,
    finalTitle,
    finalOgTitle,
    finalImageUrl,
    siteName,
    finalOgType,
    ogLocale,
    robotsContent,
    canonicalUrl,
    currentUrl,
    ampUrl,
    prevPage,
    nextPage,
    page,
    article,
    publishedTime,
    modifiedTime,
    author,
    section,
    tags,
    hreflangUrls,
    allSchemas,
    currentMeta.description,
    currentMeta.keywords,
    currentMeta.ogDescription
  ]);

  return null;
};

export default SEO;

